import React, { useState, useRef } from 'react';
import { Database, UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, ArrowRight, Loader2, ListTree } from 'lucide-react';
import { useCRM } from '../context/CRMContext';
import { useAuth, ROLES } from '../context/AuthContext';

const DataHub = () => {
  const { user } = useAuth();
  const { importPolicies } = useCRM();
  
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isInjecting, setIsInjecting] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  
  const fileInputRef = useRef(null);

  // Parsea el CSV directamente en el navegador (Sin dependencias costosas)
  const parseCSVFile = async (selectedFile) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const lines = text.replace(/\r\n/g, '\n').split('\n');
          if(lines.length < 2) throw new Error("Archivo vacío o muy corto");
          
          // Soporte estándar Excel (;) o Google Sheets (,)
          const delimiter = lines[0].includes(';') ? ';' : ',';
          
          // Standardize headers
          const rawHeaders = lines[0].split(delimiter);
          const headers = rawHeaders.map(h => {
             let clean = h.trim().toLowerCase().replace(/["\r]/g, '');
             if(clean.includes('nombre')) return 'nombre';
             if(clean.includes('telef') || clean.includes('teléf')) return 'telefono';
             if(clean.includes('mail') || clean.includes('correo')) return 'email';
             if(clean.includes('tipo') || clean.includes('seguro')) return 'tipo';
             if(clean.includes('valor') || clean.includes('prima')) return 'valor';
             return clean;
          });
          
          const results = [];
          for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;
            
            let row = [];
            let inQuotes = false;
            let currentVal = '';
            for (let char of line) {
              if (char === '"') inQuotes = !inQuotes;
              else if (char === delimiter && !inQuotes) { row.push(currentVal); currentVal = ''; }
              else currentVal += char;
            }
            row.push(currentVal);

            const obj = {};
            headers.forEach((header, index) => {
               obj[header] = row[index] ? row[index].replace(/^"|"$/g, '').trim() : '';
            });
            
            // Validar que al menos tenga nombre
            if (obj.nombre) {
              results.push(obj);
            }
          }
          resolve(results);
        } catch (err) {
          reject(err);
        }
      };
      reader.readAsText(selectedFile);
    });
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  
  const handleDrop = async (e) => {
    e.preventDefault(); e.stopPropagation();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.name.endsWith('.csv') || droppedFile.type === 'text/csv')) {
      processFile(droppedFile);
    } else {
      alert("Por favor sube solo archivos .csv");
    }
  };

  const handleChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      processFile(selectedFile);
    }
  };

  const processFile = async (targetFile) => {
    setFile(targetFile);
    setIsProcessing(true);
    setUploadSuccess(false);
    
    try {
      // Simular delay de parseo masivo para el demo UI
      setTimeout(async () => {
        const data = await parseCSVFile(targetFile);
        setCsvData(data);
        setIsProcessing(false);
      }, 1500);
    } catch(err) {
      alert("Error leyendo archivo. Verifica el formato.");
      setIsProcessing(false);
      setFile(null);
    }
  };

  const handleInject = async () => {
    if (csvData.length === 0) return;
    setIsInjecting(true);
    
    // Convertir el JSON mapeado a la estructura que usa Supabase
    const payload = csvData.map(c => ({
      clientName: c.nombre,
      phone: c.telefono || '+56 9 0000 0000',
      email: c.email || '',
      type: c.tipo || 'Auto',
      value: c.valor || '0'
    }));

    // Inyectar a Contexto / Base de Datos
    await importPolicies(payload);

    setTimeout(() => {
      setIsInjecting(false);
      setUploadSuccess(true);
      setCsvData([]);
    }, 2000);
  };

  if (user?.role === ROLES.VIEWER) {
    return (
      <div style={{ textAlign: 'center', marginTop: '4rem' }}>
        <h2>Acceso Denegado</h2>
        <p className="text-muted">No tienes permiso para importar bases de datos operativas.</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <Database size={28} color="var(--primary)" />
        <div>
          <h1>Data Hub Corporativo</h1>
          <p className="text-muted">Importador de CSV y Excel. Carga miles de prospectos (leads) al pipeline instantáneamente.</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '2rem', flexDirection: 'column' }}>
        
        {/* ZONA DE CARGA */}
        {!csvData.length && !uploadSuccess && (
          <div 
            className="card"
            style={{ 
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
              padding: '4rem 2rem', textAlign: 'center', border: '2px dashed var(--primary)', 
              backgroundColor: 'var(--bg-color)', cursor: 'pointer', transition: 'all 0.2s'
            }}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input 
              type="file" 
              accept=".csv" 
              ref={fileInputRef} 
              style={{ display: 'none' }} 
              onChange={handleChange}
            />
            
            {isProcessing ? (
              <div style={{ color: 'var(--primary)' }}>
                <Loader2 size={48} className="spin-anim" style={{ margin: '0 auto 1.5rem auto' }} />
                <h3>Procesando Archivo...</h3>
                <p className="text-muted">Mapeando columnas y normalizando comas e hileras de datos.</p>
              </div>
            ) : (
              <>
                <UploadCloud size={64} color="var(--primary)" style={{ marginBottom: '1.5rem' }} />
                <h3 style={{ marginBottom: '0.5rem' }}>Haz Clic o Arrastra tu documento CSV aquí</h3>
                <p className="text-muted" style={{ maxWidth: '400px', margin: '0 auto' }}>
                  Asegúrate que la primera fila contenga: Nombre, Telefono, Email, Tipo, Valor. El motor autodetectará tabuladores.
                </p>
              </>
            )}
          </div>
        )}

        {/* MENSAJE EXITO */}
        {uploadSuccess && (
          <div className="card animate-fade-in" style={{ textAlign: 'center', padding: '3rem', backgroundColor: 'var(--success-bg)', border: '1px solid var(--success)' }}>
            <CheckCircle2 size={64} color="var(--success)" style={{ margin: '0 auto 1.5rem auto' }} />
            <h2 style={{ color: 'var(--success)', marginBottom: '1rem' }}>¡Inyección de Datos Exitosa!</h2>
            <p style={{ color: 'var(--text-main)', marginBottom: '2rem' }}>
              Los {file?.name} prospectos han sido consolidados exitosamente en PostgreSQL y estarán a la espera en la primera columna del Pipeline de tus Ejecutivos.
            </p>
            <button className="btn btn-primary" onClick={() => { setUploadSuccess(false); setFile(null); }} style={{ margin: '0 auto' }}>Procesar Otra Planilla</button>
          </div>
        )}

        {/* ZONA DE VISTA PREVIA Y MAPEADO */}
        {csvData.length > 0 && !uploadSuccess && (
          <div className="card animate-fade-in">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <FileSpreadsheet size={24} color="var(--primary)" />
                <div>
                  <h3 style={{ margin: 0 }}>Vista Previa Estructural</h3>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Archivo: {file?.name} ({csvData.length} filas válidas)</span>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button className="btn" onClick={() => { setCsvData([]); setFile(null); }}>Cancelar</button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleInject}
                  disabled={isInjecting}
                >
                  {isInjecting ? (
                    <><Loader2 size={18} className="spin-anim" /> Inyectando Servidor...</>
                  ) : (
                    <>Inyectar a la Base de Datos <ArrowRight size={18} /></>
                  )}
                </button>
              </div>
            </div>

            <div style={{ padding: '1rem', backgroundColor: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: '6px', marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <AlertTriangle size={24} color="var(--warning)" />
              <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-main)' }}>
                <strong>Revisión Crítica:</strong> El motor ha extraído los primeros 5 registros exitosamente para tu revisión. Las columnas faltantes en tu CSV o valores nulos se inyectarán como espacios en blanco. Si está correcto, procede a inyectar el total ({csvData.length}).
              </p>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse', fontSize: '0.9rem', backgroundColor: 'var(--bg-color)', borderRadius: '6px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    <th style={{ padding: '1rem', fontWeight: 600 }}>1. NOMBRE (Parseado)</th>
                    <th style={{ padding: '1rem', fontWeight: 600 }}>2. TELEFONO (Parseado)</th>
                    <th style={{ padding: '1rem', fontWeight: 600 }}>3. CORREO (Parseado)</th>
                    <th style={{ padding: '1rem', fontWeight: 600 }}>4. TIPO (Parseado)</th>
                    <th style={{ padding: '1rem', fontWeight: 600 }}>5. VALOR PREVISTO</th>
                  </tr>
                </thead>
                <tbody>
                  {csvData.slice(0, 5).map((row, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '1rem', fontWeight: 500, color: 'var(--primary)' }}>{row.nombre || <span className="text-muted italic">Vacio</span>}</td>
                      <td style={{ padding: '1rem' }}>{row.telefono || <span className="text-muted italic">Vacio</span>}</td>
                      <td style={{ padding: '1rem' }}>{row.email || <span className="text-muted italic">Vacio</span>}</td>
                      <td style={{ padding: '1rem' }}>{row.tipo || <span className="text-muted italic">Vacio</span>}</td>
                      <td style={{ padding: '1rem' }}>{row.valor || <span className="text-muted italic">Vacio</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {csvData.length > 5 && (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', borderTop: '1px solid var(--border)' }}>
                  ... y {csvData.length - 5} prospectos más ocultos para fluidez.
                </div>
              )}
            </div>
            
          </div>
        )}

      </div>
    </div>
  );
};

export default DataHub;
